import { type RootStore } from '@littlebird/sdk';
import { Priority } from 'kbar';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { getEnvVar } from '@/env';
import { ShareChatModal } from '@/features/chat/chat.modals';
import { confirmChatListDeletion } from '@/features/settings/proactive-agent-chat-deletion';
import { littlebirdSdk } from '@/lib/sdk-init';
import { userStore } from '@/stores/user.store';
import { saveBlob } from '@/utils/file-io';
import { navigate } from '@/utils/navigation-service';

import { type CommandBarContext } from './types';

import { createAction, Section } from '.';

const getThread = ({ threadId }: { stores: RootStore; threadId: string }) => {
  const thread = littlebirdSdk.stores.threads.data.get(threadId);
  return thread;
};

export const getThreadId = ({ location, threadId }: CommandBarContext) => {
  if (threadId) return threadId;
  const match = location.pathname.match(/^\/chats\/([^/]+)$/);
  return match ? match[1] : undefined;
};

const chatSection = (context: CommandBarContext) => {
  const { stores } = context;
  const threadId = getThreadId(context);
  if (!threadId) return Section.Chat;
  const thread = getThread({ stores, threadId });
  return thread?.title ?? 'Chat';
};

const chatVisible = (context: CommandBarContext) => {
  const { stores } = context;
  const threadId = getThreadId(context);
  if (!threadId) return false;
  return !!getThread({ stores, threadId });
};

export const startNewChat = createAction({
  id: 'start-new-chat',
  name: 'New Chat',
  section: 'Actions',
  keywords: 'new chat compose start',
  icon: <Icons.newChat size={14} />,
  perform: () => {
    navigate({ to: '/chats', search: { new: true } });
  },
  priority: Priority.HIGH,
});

export const starChat = createAction({
  id: 'star-chat',
  section: chatSection,
  name: (context) => {
    const threadId = getThreadId(context);
    if (!threadId) return '';
    const thread = getThread({ stores: context.stores, threadId });
    return thread?.starred ? 'Unstar chat' : 'Star chat';
  },
  visible: chatVisible,
  icon: (context) => {
    const threadId = getThreadId(context);
    if (!threadId) return;
    const thread = getThread({ stores: context.stores, threadId });
    return thread?.starred ? (
      <Icons.starFilled className="size-3.5" />
    ) : (
      <Icons.star className="size-3.5" />
    );
  },
  perform: (context) => {
    const threadId = getThreadId(context);
    if (!threadId) return;
    const thread = getThread({ stores: context.stores, threadId });
    if (!thread) return;
    littlebirdSdk.stores.threads.updateThread(thread, {
      starred: !thread.starred,
    });
  },
  priority: 5,
});

export const shareChat = createAction({
  id: 'share-chat',
  section: chatSection,
  name: 'Share…',
  keywords: 'share chat',
  visible: (context) => !userStore.hasSignedBaa && chatVisible(context),
  icon: <Icons.share size={14} />,
  perform: (context) => {
    const threadId = getThreadId(context);
    if (!threadId) return;
    const thread = getThread({ stores: context.stores, threadId });
    if (!thread) return;
    ModalsManager.show(ShareChatModal, {
      chatIds: [thread.id],
    });
  },
  priority: 4,
});

export const deleteChat = createAction({
  id: 'delete-chat',
  section: chatSection,
  name: 'Delete…',
  keywords: 'delete chat',
  visible: chatVisible,
  icon: <Icons.trash size={14} />,
  perform: async (context) => {
    const threadId = getThreadId(context);
    if (!threadId) return;
    const thread = getThread({ stores: context.stores, threadId });
    if (!thread || !(await confirmChatListDeletion([threadId]))) return;
    littlebirdSdk.stores.threads.delete(thread);
    navigate({ to: '/chats' });
  },
  priority: 1,
});

export const copyChatId = createAction({
  id: 'copy-chat-id',
  name: 'ID',
  keywords: 'copy chat id',
  section: chatSection,
  visible: chatVisible,
  icon: <Icons.copy size={14} />,
  perform: (context) => {
    const threadId = getThreadId(context);
    if (!threadId) return;
    navigator.clipboard.writeText(threadId);
    toast.success('Chat ID copied');
  },
  priority: Priority.NORMAL,
});

export const copyChatLink = createAction({
  id: 'copy-chat-link',
  name: 'Link',
  keywords: 'copy chat link',
  section: chatSection,
  visible: chatVisible,
  icon: <Icons.copy size={14} />,
  perform: (context) => {
    const threadId = getThreadId(context);
    if (!threadId) return;
    const baseUrl =
      getEnvVar('BASE_URL_FOR_PUBLIC_LINKS') ?? window.location.origin;
    const chatLink = new URL(`/chats/${threadId}`, baseUrl).toString();
    navigator.clipboard.writeText(chatLink);
    toast.success('Chat link copied');
  },
  priority: Priority.NORMAL,
});

export const exportChatAsMarkdown = createAction({
  id: 'export-chat-as-markdown',
  icon: <Icons.download size={14} />,
  section: chatSection,
  name: 'Export as Markdown',
  keywords: 'export chat as markdown',
  visible: chatVisible,
  perform: (context) => {
    const threadId = getThreadId(context);
    if (!threadId) return;
    const thread = getThread({ stores: context.stores, threadId });
    if (!thread) return;
    const messages = littlebirdSdk.stores.messages.getMessagesByThreadId(
      thread.id
    );
    const markdown = messages
      .map(
        (m) =>
          `${m.role === 'user' ? '**You**: ' : '**Littlebird**: '}${m.content ?? ''}`
      )
      .join('\n\n\n\n');
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const safeTitle = (thread.title || 'conversation').replace(
      /[\\/:*?"<>|]+/g,
      '_'
    );
    void saveBlob(blob, `${safeTitle}.md`).catch(() => {
      toast.error('Failed to export chat');
    });
  },
  priority: 3,
});

export const copyChat = createAction({
  id: 'copy-chat',
  name: 'Copy…',
  keywords: 'copy chat',
  section: chatSection,
  visible: chatVisible,
  icon: <Icons.copy size={14} />,
  children: [copyChatLink, copyChatId],
  priority: 2,
});

export const chatActions = [
  starChat,
  shareChat,
  exportChatAsMarkdown,
  copyChat,
  deleteChat,
].map((action) => ({ ...action, contextKind: 'Chat' as const }));

export const routineChatActions = chatActions
  .filter((action) => action.id !== deleteChat.id)
  .map((action) => ({ ...action, contextKind: 'Routine' as const }));
